import React, { useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import { Button, Col, Form, Modal, Row, Select } from 'antd';
import PropTypes from 'prop-types';

import { UploadField } from '../../../components/SharedComponents/FormComponents/UploadField';

import { ArrowRight, ChevronDown, ChevronUp } from '../../../assets/icons';
import { updateUserProfile } from '../../../services/authApi/userApi';
import { addUserData } from '../../../store/reducer/accountSlice';
import { toastNotify } from '../../../utils/toastNotify';
import { ENTITY_DOC_UPLOAD_STATUS, uploadEntityDocuments } from '../../../utils/entityDocumentUpload';
import { isPasswordProtectedPdf } from '../../../utils/pdfPasswordCheck';
import EntityDocumentItem from '../../../components/EntityDocumentItem/EntityDocumentItem';
import { ENTITY_DOC_UPLOAD_VALIDATION } from '../../../utils/fileValidation';
import { getEntityDocLimitMessage, selectEntityDocuments } from '../../../utils/entityDocumentSelection';
import {
  ENTITY_DOCUMENT_TYPE_OPTIONS,
  ENTITY_DOC_DUPLICATE_TOAST_KEY,
  ENTITY_DOC_LIMIT_TOAST_KEY,
  MAX_ENTITY_DOCUMENTS,
} from '../../../constants/accountType.constants';

const EntityDocs = (props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [arrowUp, setArrowUp] = useState(false);
  const [docType, setDocType] = useState(ENTITY_DOCUMENT_TYPE_OPTIONS[0]);
  const [fileList, setFileList] = useState([]);
  const [addedDocuments, setAddedDocuments] = useState([]);
  // Per-document upload progress keyed by uid: { state, percent }.
  const [uploadStatuses, setUploadStatuses] = useState({});
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [fileValidate, setFileValidate] = useState(false);
  const [fileSizeValidate, setFileSizeValidate] = useState(false);
  const [passwordProtectedValidate, setPasswordProtectedValidate] = useState(false);
  const latestSelectionRef = useRef(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const dispatch = useDispatch();

  const onChangeHandle = async ({ fileList: newFileList }) => {
    const selectionId = latestSelectionRef.current + 1;
    latestSelectionRef.current = selectionId;

    if (!newFileList.length) {
      setFileList([]);
      return;
    }

    const remainingSlots = MAX_ENTITY_DOCUMENTS - addedDocuments.length;
    if (remainingSlots <= 0) {
      toastNotify('info', `You can only add up to ${MAX_ENTITY_DOCUMENTS} documents.`, ENTITY_DOC_LIMIT_TOAST_KEY);
      setFileList([]);
      return;
    }

    // Drop password-protected PDFs before taking slots, so a locked file doesn't use one up.
    const passwordChecks = await Promise.all(newFileList.map((item) => isPasswordProtectedPdf(item?.originFileObj)));
    // AntD calls onChange once per selected file with a growing list; only the latest call
    // (which holds every selected file) is applied, whatever order the async checks finish in.
    if (selectionId !== latestSelectionRef.current) return;
    const selection = selectEntityDocuments({ fileList: newFileList, passwordChecks, addedDocuments, docType });

    setPasswordProtectedValidate(selection.hasPasswordProtected);
    setFileValidate(selection.hasInvalidFormat);
    setFileSizeValidate(selection.hasInvalidSize);

    if (selection.skippedForLimit.length) {
      // AntD calls onChange once per selected file, so key the toast to show it only once per selection.
      toastNotify('info', getEntityDocLimitMessage(selection.skippedForLimit), ENTITY_DOC_LIMIT_TOAST_KEY);
    }

    if (selection.hasDuplicate) {
      toastNotify('info', 'This document has already been added.', ENTITY_DOC_DUPLICATE_TOAST_KEY);
    }

    if (selection.documentsToAdd.length) {
      setAddedDocuments([...addedDocuments, ...selection.documentsToAdd]);
    }

    setFileList([]);
  };

  const beforeUpload = () => false;

  const handleRemoveUploaded = (fileId) => {
    setFileList((prevFileList) => prevFileList.filter((item) => item.uid !== fileId));
    setFileValidate(false);
    setFileSizeValidate(false);
    setPasswordProtectedValidate(false);
  };

  const handleRemoveAddedDocument = (fileId) => {
    setAddedDocuments((prevDocuments) => prevDocuments.filter((item) => item.uid !== fileId));
  };

  const handlePreviewDocument = (documentItem) => {
    if (!documentItem?.originFileObj) {
      return;
    }

    const previewUrl = URL.createObjectURL(documentItem.originFileObj);
    setPreviewDocument({
      ...documentItem,
      previewUrl,
      mimeType: documentItem.originFileObj.type,
    });
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    if (previewDocument?.previewUrl) {
      URL.revokeObjectURL(previewDocument.previewUrl);
    }
    setPreviewOpen(false);
    setPreviewDocument(null);
  };

  const isContinueDisabled = addedDocuments.length === 0;

  const handleDocumentStatusChange = (uid, status) => {
    setUploadStatuses((prevStatuses) => ({ ...prevStatuses, [uid]: status }));
  };

  // Marks every document as Waiting, then uploads them one by one so each card shows its own progress.
  const uploadDocumentsWithProgress = async () => {
    setUploadStatuses(Object.fromEntries(addedDocuments.map((item) => [item.uid, { state: ENTITY_DOC_UPLOAD_STATUS.WAITING }])));
    setIsUploadingDocs(true);
    try {
      return await uploadEntityDocuments(addedDocuments, handleDocumentStatusChange);
    } finally {
      setIsUploadingDocs(false);
    }
  };

  const onEntityDocsSubmit = async () => {
    setLoading(true);

    try {
      const uploadedDocs = await uploadDocumentsWithProgress();
      const profileResp = await updateUserProfile({ entityDocs: uploadedDocs });
      if (profileResp.isError) {
        toastNotify('error', profileResp?.message);
      } else {
        dispatch(addUserData({ userData: profileResp?.data?.user }));
        props.nextAction('StepTwo');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'An error occurred while uploading your document. Please try again';
      toastNotify('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} className='auth-form entitydocs' name='entityDocsForm' onFinish={onEntityDocsSubmit}>
      <p>To verify your entity and comply with federal regulations, please upload supporting documentation.</p>
      <p>Documents must be clear, uncropped, and show the legal name of your entity.</p>
      <p className='fw-600 mb-8'>Examples of accepted documents</p>
      <ul style={{ marginLeft: '20px', marginBottom: '24px' }}>
        {ENTITY_DOCUMENT_TYPE_OPTIONS.map((option) => (
          <li key={option}>{option}</li>
        ))}
      </ul>
      <p className='mb-16'>You can upload up to {MAX_ENTITY_DOCUMENTS} supporting documents.</p>
      {addedDocuments.length < MAX_ENTITY_DOCUMENTS && (
        <>
          <div id='entity-doc-type-anchor' />
          <label htmlFor='entityDocType' className='entitydocs__label'>
            Type of document
          </label>
          <Form.Item name='docType' initialValue={docType}>
            <Select
              id='entityDocType'
              onChange={(value) => setDocType(value)}
              value={docType}
              getPopupContainer={() => document.getElementById('entity-doc-type-anchor')}
              onDropdownVisibleChange={() => setArrowUp(!arrowUp)}
              suffixIcon={arrowUp ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
            >
              {ENTITY_DOCUMENT_TYPE_OPTIONS.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Row>
            <Col span={24}>
              <label htmlFor='entityDoc' className='sr-only'>
                Upload document
              </label>
              <Form.Item className='entity-doc'>
                <UploadField
                  onChangeHandle={onChangeHandle}
                  fileList={fileList}
                  id='entityDoc'
                  beforeUpload={beforeUpload}
                  handleRemoveUploaded={() => handleRemoveUploaded(fileList[0].uid)}
                  label='Upload document'
                  fileValidate={fileValidate}
                  fileSizeValidate={fileSizeValidate}
                  passwordProtectedValidate={passwordProtectedValidate}
                  loading={loading}
                  accept='image/png,image/jpeg,image/jpg,application/pdf'
                  validationMessages={ENTITY_DOC_UPLOAD_VALIDATION}
                  variant='dropzone'
                  multiple
                  hint='Accepted file types: JPG, JPEG, PNG, PDF • Max file size: 10MB per file'
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {addedDocuments.length > 0 && (
        <div className='entitydocs__added-list'>
          <p className='fw-600 mb-8'>Added documents</p>
          {addedDocuments.map((documentItem) => (
            <EntityDocumentItem
              key={documentItem.uid}
              documentItem={documentItem}
              uploadStatus={uploadStatuses[documentItem.uid]}
              isUploading={isUploadingDocs || (loading && !!uploadStatuses[documentItem.uid])}
              onPreview={() => handlePreviewDocument(documentItem)}
              onRemove={() => handleRemoveAddedDocument(documentItem.uid)}
            />
          ))}
        </div>
      )}
      {addedDocuments.length >= MAX_ENTITY_DOCUMENTS && (
        <p className='entitydocs__limit-note mb-0'>You have reached the {MAX_ENTITY_DOCUMENTS} document maximum.</p>
      )}
      <Form.Item className='auth-form__fixed-submit'>
        <Button type='primary' htmlType='submit' className='ant-btn-primary--alt' loading={loading} disabled={isContinueDisabled}>
          Continue <ArrowRight marginLeft='10px' verticalAlign='middle' top='-2px' />
        </Button>
      </Form.Item>
      <Modal open={previewOpen} onCancel={handleClosePreview} footer={null} width={900} centered title={previewDocument?.fileName}>
        <div style={{ height: '70vh', overflow: 'auto' }}>
          {previewDocument?.mimeType === 'application/pdf' ? (
            <iframe
              src={previewDocument?.previewUrl}
              title={previewDocument?.fileName}
              style={{ width: '100%', height: '100%', minHeight: '65vh', border: 0 }}
            />
          ) : (
            <img
              src={previewDocument?.previewUrl}
              alt={previewDocument?.fileName}
              style={{ width: '100%', maxHeight: '65vh', objectFit: 'contain' }}
            />
          )}
        </div>
      </Modal>
    </Form>
  );
};

EntityDocs.propTypes = {
  nextAction: PropTypes.func,
};

export default EntityDocs;
